import styled from '@emotion/styled';
import React, { useCallback, useEffect, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ModalSidebarContent } from '../../../../components/ModalSidebar';
import { EmployeeOption } from '../../../../components/Selects/EmployeeOptionLabel';
import FlairIcon from '../../../../../../atomic/atoms/FlairIcon';
import { ObjectivePendingAction } from '../../../../__generated__/graphql';
import { useToasts } from '../../../../../../context/Toast';
import { useUserInfo } from '../../../../context/UserInfo';
import { GoalsActivities } from '../GoalsActivities/GoalsActivities';
import { GoalActivity, GoalId, GoalRelatedGoal } from '../types';
import { GoalOptions } from '../useGoalSelectOptions';
import { GoalCommonFields } from './GoalCommonFields';
import { GoalSidebarMode, ObjectiveFormData } from './types';
import { useSubmitUpdate, useUpdateObjectiveForm } from './useObjectiveForm';
import { GoalHierarchy } from './GoalHierarchy/GoalHierarchy';
import { GoalHierarchyItem } from './GoalHierarchy/types';
import { GoalAlignedToConnected } from './GoalAlignedTo/GoalAlignedToConnected';
import { useGoalsContext } from '../GoalsContext/GoalsContext';
import { GoalFormProvider, useGoalFormContext } from './goalFormState';
import { GoalApprovalActions } from './GoalApprovalActions';

type Props = {
  owner?: EmployeeOption;
  goalId: GoalId;
  mode: GoalSidebarMode;
  options: GoalOptions;
  formData: ObjectiveFormData;
  goalWithHierarchy: GoalHierarchyItem;
  activities: GoalActivity[];
  onClose: () => void;
  alignedTo: GoalRelatedGoal[];
  useOkr?: boolean;
};

export const GoalUpdateForm: React.FC<React.PropsWithChildren<Props>> = (
  props,
) => {
  const form = useUpdateObjectiveForm(props.formData);

  const { resetForm } = form;

  useEffect(() => {
    resetForm(props.formData);
  }, [resetForm, props.formData]);

  return (
    <GoalFormProvider form={form}>
      <Form>
        <GoalUpdateFormContent {...props} />
      </Form>
    </GoalFormProvider>
  );
};

const GoalUpdateFormContent: React.FC<React.PropsWithChildren<Props>> = ({
  goalId,
  formData,
  goalWithHierarchy,
  mode,
  activities,
  onClose,
  alignedTo,
  options,
  ...restProps
}) => {
  const { t } = useTranslation();
  const form = useGoalFormContext<ObjectiveFormData>();
  const { developmentFrameworkEnabled } = useGoalsContext();
  const { id: meId } = useUserInfo();
  const { addSuccess } = useToasts();

  const { submitField } = useSubmitUpdate(meId, goalId);

  // Local optimistic state for alignedTo — updated immediately on add/remove,
  // then synced from props when the query re-renders after mutation completes.
  const [localAlignedTo, setLocalAlignedTo] =
    useState<GoalRelatedGoal[]>(alignedTo);
  useEffect(() => {
    setLocalAlignedTo(alignedTo);
  }, [alignedTo]);

  const [isAlignmentProcessing, setIsAlignmentProcessing] = useState(false);

  const handleAlignmentAdd = useCallback(
    async (goal: GoalRelatedGoal) => {
      const newAlignedTo = [...localAlignedTo, goal];
      setLocalAlignedTo(newAlignedTo);
      const alignedToIds = newAlignedTo.map((g) => ({
        id: g.id,
        type: g.type,
      }));
      setIsAlignmentProcessing(true);
      try {
        await submitField('alignedTo', alignedToIds);
        addSuccess(t('performanceReview.goals2.goalAlignedTo.addSuccess'));
      } finally {
        setIsAlignmentProcessing(false);
      }
    },
    [localAlignedTo, submitField, addSuccess, t],
  );

  const handleAlignmentRemove = useCallback(
    async (goal: GoalRelatedGoal) => {
      const newAlignedTo = localAlignedTo.filter((g) => g.id !== goal.id);
      setLocalAlignedTo(newAlignedTo);
      const alignedToIds = newAlignedTo.map((g) => ({
        id: g.id,
        type: g.type,
      }));
      setIsAlignmentProcessing(true);
      try {
        await submitField('alignedTo', alignedToIds);
      } finally {
        setIsAlignmentProcessing(false);
      }
    },
    [localAlignedTo, submitField],
  );

  const goalHierarchy = (
    <div className="mb-3">
      <GoalHierarchy rootGoal={goalWithHierarchy} currentGoalId={goalId.id} />
    </div>
  );

  const goalAlignedTo =
    goalId.type === 'employee' && developmentFrameworkEnabled ? (
      <div className="mb-3">
        <GoalAlignedToConnected
          goalId={goalId}
          alignedTo={localAlignedTo}
          onAdd={handleAlignmentAdd}
          onRemove={handleAlignmentRemove}
          isProcessing={isAlignmentProcessing}
        />
      </div>
    ) : null;

  const { pendingApprovalAction, approvalRequestId } = form.values;
  const { isManagerView } = useGoalsContext();

  const handleApprovalComplete = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <ModalSidebarContent
      body={
        <div>
          {pendingApprovalAction != null && (
            <div className="mb-3">
              <Alert
                variant="warning"
                className={
                  isManagerView && approvalRequestId ? 'mb-2' : 'mb-0'
                }>
                <div className="d-flex align-items-center gap-2">
                  <FlairIcon icon="hour-glass-outline" size="xs" />
                  {pendingApprovalAction === ObjectivePendingAction.ObjCreate
                    ? t(
                        'performanceReview.goals2.approvalStatus.pendingCreation',
                      )
                    : pendingApprovalAction === ObjectivePendingAction.ObjUpdate
                    ? t('performanceReview.goals2.approvalStatus.pendingUpdate')
                    : t(
                        'performanceReview.goals2.approvalStatus.pendingDeletion',
                      )}
                </div>
              </Alert>
              {isManagerView && approvalRequestId && (
                <GoalApprovalActions
                  approvalRequestId={approvalRequestId}
                  onComplete={handleApprovalComplete}
                />
              )}
            </div>
          )}
          <input type="hidden" value={form.values.type} readOnly />
          <GoalCommonFields
            {...restProps}
            mode={mode}
            submitField={submitField}
            goalHierarchy={goalHierarchy}
            goalAlignedTo={goalAlignedTo}
            options={options}
            currentGoalId={goalId.id}
          />
          <ActivityHeader className="mb-3">
            {t('performanceReview.goals2.activityTitle')}
          </ActivityHeader>
          <GoalsActivities activities={activities} />
        </div>
      }
      footer={null}
    />
  );
};

const Form = styled('form')({
  display: 'contents',
});

const ActivityHeader = styled('div')({
  fontWeight: 600,
  fontSize: 15,
});
